import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Card } from 'react-bootstrap';
import {
  FaCog,
  FaDatabase,
  FaUserShield,
  FaTable,
  FaThLarge,
  FaBolt,
  FaStar,
  FaUsers,
  FaUserCog,
  FaUserFriends,
  FaEnvelope,
  FaFileAlt,
  FaChevronRight,
  FaChevronLeft,
  FaSitemap,
  FaHome,
  FaWpforms,
  FaSearch,
  FaClock,
  FaDollarSign,
  FaSlidersH,
  FaListOl,
  FaPhone,
  FaLayerGroup,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import './css/SetupPage.css';
import { getEntitlementsSection, canAccessSetup, hasPackage } from '../../utils/entitlements';

const SetupPage = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('objectmanager');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const menuItems = [
    {
      id: 'objectmanager',
      label: 'Object Manager',
      icon: <FaDatabase />,
      path: '/setup/objectmanager'
    },
       {
      id: 'layout-recordview-roles',
      label: 'Layout & record view access',
      icon: <FaLayerGroup />,
      path: '/setup/layout-recordview-roles'
    },
    {
      id: 'rolemanager',
      label: 'Role Manager',
      icon: <FaUserShield />,
      path: '/setup/rolemanager'
    },
    {
      id: 'tabs',
      label: 'Tab Manager',
      icon: <FaTable />,
      path: '/setup/tabs'
    },
    {
      id: 'appmanager',
      label: 'App Manager',
      icon: <FaThLarge />,
      path: '/setup/appmanager'
    },
    {
      id: 'actions',
      label: 'Action Manager',
      icon: <FaBolt />,
      path: '/setup/actions'
    },
    {
      id: 'workflows',
      label: 'Workflow Manager',
      icon: <FaSitemap />,
      path: '/setup/workflows'
    },
    {
      id: 'homewidget',
      label: 'Edit Home',
      icon: <FaHome />,
      path: '/setup/homewidget'
    },
    {
      id: 'webforms',
      label: 'Web Forms',
      icon: <FaWpforms />,
      path: '/setup/webforms'
    },
    {
      id: 'scoringrule',
      label: 'Scoring Rules',
      icon: <FaStar />,
      path: '/setup/scoringrule'
    },
    {
      id: 'usergroups',
      label: 'User Groups',
      icon: <FaUsers />,
      path: '/setup/usergroups'
    },
    {
      id: 'users',
      label: 'Users',
      icon: <FaUserFriends />,
      path: '/setup/users'
    },
    {
      id: 'mailconfig',
      label: 'Mail Config',
      icon: <FaEnvelope />,
      path: '/setup/mailconfig'
    },
    {
      id: 'emailtemplate',
      label: 'Email Template',
      icon: <FaFileAlt />,
      path: '/setup/emailtemplate'
    },
    {
      id: 'assignmentrule',
      label: 'Assignment Rules',
      icon: <FaUserCog />,
      path: '/setup/assignmentrule'
    },
    {
      id: 'searchviews',
      label: 'Search Views',
      icon: <FaSearch />,
      path: '/setup/searchviews'
    },
    {
      id: 'session_setting',
      label: 'Session Settings',
      icon: <FaClock />,
      path: '/setup/session_setting'
    },
    { id: 'currency', label: 'Currency', icon: <FaDollarSign />, isGroupHeader: true },
    { id: 'currencymaster', label: 'Manage Currency', path: '/setup/currency/master', parentId: 'currency' },
    { id: 'currencymanager', label: 'Manage Exchange Rate', path: '/setup/currency/exchange-rate', parentId: 'currency' },
    {
      id: 'system_settings',
      label: 'System Settings',
      icon: <FaSlidersH />,
      path: '/setup/system_settings'
    },
    {
      id: 'stage_master',
      label: 'Probability Manager',
      icon: <FaListOl />,
      path: '/setup/stage_master'
    },
    {
      id: 'telecmi',
      label: 'TeleCMI',
      icon: <FaPhone />,
      path: '/setup/telecmi'
    },

  ];

  const setupConfig = getEntitlementsSection('setup', {});
  const workflowEnt = getEntitlementsSection('workflow', {});
  const hasSetupConfig = canAccessSetup() && setupConfig && (setupConfig.allowedItems?.length > 0 || setupConfig.mode === 'denylist');
  const allowedSetupItems = new Set(
    (setupConfig?.allowedItems || []).map(item => String(item).toLowerCase())
  );
  const isAllowlist = setupConfig?.mode === 'allowlist';
  const isDenylist = setupConfig?.mode === 'denylist';

  // Workflow menu item: only show when in allowed list (allowlist) or not in deny list (denylist) AND view allowed
  const workflowsInAllowedList = allowedSetupItems.has('workflows');
  const showWorkflowsItem =
    workflowEnt.allowViewWorkflow === true &&
    (isAllowlist ? workflowsInAllowedList : isDenylist ? !workflowsInAllowedList : workflowsInAllowedList);

  // Manage Currency is always shown. Currency group (header + Manage Exchange Rate) only when Multi Currency is enabled (subscription).
  // When mode is missing/other, default to false so the group is hidden unless explicitly in allowedItems.
  const showCurrencyGroup = isAllowlist
    ? allowedSetupItems.has('currency')
    : isDenylist
      ? !allowedSetupItems.has('currency')
      : false;

  const filteredMenuItems = (isAllowlist
    ? menuItems.filter(item => {
        if (item.id === 'telecmi') return hasPackage('telecmi');
        if (item.id === 'currencymaster') return true;
        if (item.id === 'currency' || item.id === 'currencymanager') return showCurrencyGroup;
        if (item.id === 'layout-recordview-roles') {
          return (
            allowedSetupItems.has('objectmanager') ||
            allowedSetupItems.has('layout-recordview-roles')
          );
        }
        return allowedSetupItems.has(item.id.toLowerCase());
      })
    : isDenylist
      ? menuItems.filter(item => {
          if (item.id === 'telecmi') return hasPackage('telecmi');
          if (item.id === 'currencymaster') return true;
          if (item.id === 'currency' || item.id === 'currencymanager') return showCurrencyGroup;
          if (item.id === 'layout-recordview-roles') {
            return !allowedSetupItems.has('objectmanager');
          }
          return !allowedSetupItems.has(item.id.toLowerCase());
        })
      : menuItems.filter(item => (item.id === 'telecmi' ? hasPackage('telecmi') : true))
  ).filter(item => item.id !== 'workflows' || showWorkflowsItem);

  const hasCurrencyGroup = filteredMenuItems.some(i => i.id === 'currency' && i.isGroupHeader);

  // Set active tab based on current route
  useEffect(() => {
    if (!hasSetupConfig) {
      return;
    }
    const currentPath = location.pathname;
    if (currentPath.includes('/currency/master')) {
      setActiveTab('currencymaster');
      return;
    }
    if (currentPath.includes('/currency/exchange-rate')) {
      setActiveTab('currencymanager');
      return;
    }
    const activeItem = menuItems.find(item => item.path && currentPath.includes(item.id));
    if (activeItem) {
      setActiveTab(activeItem.id);
    }
  }, [location.pathname, hasSetupConfig]);

  const handleTabChange = (tabId) => {
    const menuItem = menuItems.find(item => item.id === tabId);
    if (menuItem && menuItem.path) {
      setActiveTab(tabId);
      window.location.href = menuItem.path;
    }
  };

  if (!hasSetupConfig) {
    return null;
  }

  return (
    <div className="setup-page-wrapper">
      {/* Dark Blue Sidebar */}
      <div className={`setup-dark-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div
          className="setup-sidebar-header"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          <FaCog className="setup-icon" />
          {!sidebarCollapsed && <span className="setup-title">Setup</span>}
        </div>

        <div className="setup-menu">
          {filteredMenuItems.map((item) => {
            if (item.isGroupHeader) {
              return (
                <div key={item.id} className="setup-menu-group-header">
                  <span className="setup-menu-icon">{item.icon}</span>
                  {!sidebarCollapsed && <span className="setup-menu-label">{item.label}</span>}
                </div>
              );
            }
            const isChild = !!item.parentId && hasCurrencyGroup;
            const isActive = activeTab === item.id;
            return (
              <div
                key={item.id}
                className={`setup-menu-item ${isActive ? 'active' : ''} ${isChild ? 'setup-menu-item-child' : ''}`}
                onClick={() => item.path && handleTabChange(item.id)}
                title={item.label}
              >
                {(isChild && !sidebarCollapsed) ? <span className="setup-menu-icon setup-menu-icon-child" /> : <span className="setup-menu-icon">{item.icon ?? (item.id === 'currencymaster' ? <FaDollarSign /> : null)}</span>}
                {!sidebarCollapsed && (
                  <>
                    <span className="setup-menu-label">{item.label}</span>
                    {isActive && <FaChevronRight className="setup-menu-arrow" />}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`setup-main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default SetupPage;
